import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Download,
  Eye,
  FileSpreadsheet,
  FolderInput,
  FolderPlus,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Share2,
  Trash2,
  Archive,
  X,
} from 'lucide-react';
import type {
  ActivityItem,
  BannerCard as BannerCardType,
  BannerKind,
  CampaignDetail,
  Folder,
} from '../../shared/types';
import { api, del, patch, post, qk } from '../lib/api';
import { activityText } from '../lib/activity';
import {
  filterBanners,
  groupBanners,
  useStoredState,
  type BannerGroup,
  type GroupBy,
  type StatusFilter,
} from '../lib/collection';
import { formatBytes, formatDate, formatName, KIND_LABELS, pluralize, relativeTime } from '../lib/format';
import { ActivityIcon, useTitle } from '../components/AppShell';
import { BannerThumb } from '../components/BannerMedia';
import { CampaignDialog } from '../components/CampaignDialog';
import { ClientBrandHeader, ClientTheme } from '../components/ClientBrand';
import { CollectionBar } from '../components/CollectionBar';
import { CommentsOverview } from '../components/CommentsOverview';
import { PresentationView, Summary } from '../components/Presentation';
import { ShareDialog } from '../components/ShareDialog';
import { UploadZone } from '../components/UploadZone';
import { errorMessage, useToast } from '../components/toast';
import {
  CheckSummary,
  ConfirmDialog,
  Dialog,
  Field,
  Menu,
  Notice,
  Spinner,
  StatusChip,
  Tabs,
} from '../components/ui';

function BannerCard({
  banner,
  campaignId,
  selected,
  onSelect,
}: {
  banner: BannerCardType;
  campaignId: string;
  selected: boolean;
  onSelect: (selected: boolean) => void;
}) {
  const v = banner.current;
  return (
    <div className={`banner-card-wrap ${selected ? 'is-selected' : ''}`}>
      <label className="banner-select" title="Auswählen">
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
          aria-label={`${banner.name} auswählen`}
        />
      </label>
      <Link to={`/kampagnen/${campaignId}/motiv/${banner.id}`} className="banner-card">
        <div className="banner-card-stage">
          {v && <BannerThumb version={v} maxWidth={240} maxHeight={188} />}
        </div>
        <div className="banner-card-body">
          <span className="eyebrow" style={{ fontSize: 11 }}>
            {formatName(banner.width, banner.height)}
            {banner.width ? ` · ${banner.width}×${banner.height}` : ''}
          </span>
          <span className="banner-card-title" title={banner.name}>
            {banner.name}
          </span>
          <div className="banner-card-meta">
            <span>{KIND_LABELS[banner.kind]}</span>
            {v && <span>{formatBytes(v.sizeBytes)}</span>}
            <span>v{v?.number ?? '–'}</span>
            {v && <CheckSummary summary={v.checkSummary} />}
          </div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <StatusChip status={banner.status} />
            {banner.openCommentCount > 0 && (
              <span className="status-icon" title="Offene Kommentare">
                <MessageSquare size={15} /> {banner.openCommentCount}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

export function ActivityList({ campaignId }: { campaignId: string }) {
  const activity = useQuery({
    queryKey: qk('activity', campaignId),
    queryFn: () => api<ActivityItem[]>(`/api/campaigns/${campaignId}/activity`),
  });
  if (activity.isLoading) return <Spinner />;
  if (!activity.data?.length) return <p className="muted">Noch keine Aktivität.</p>;
  return (
    <div className="activity">
      {activity.data.map((item) => (
        <div key={item.id} className="activity-item">
          <ActivityIcon type={item.type} />
          <div>
            <strong>{item.actorName}</strong> {activityText(item)}
            {item.internal && (
              <span className="chip chip-internal" style={{ marginLeft: 8 }}>
                intern
              </span>
            )}
            {item.bannerId && (
              <div>
                <Link className="small text-link" to={`/kampagnen/${campaignId}/motiv/${item.bannerId}`}>
                  Zum Motiv
                </Link>
              </div>
            )}
          </div>
          <span className="small muted nowrap">{relativeTime(item.createdAt)}</span>
        </div>
      ))}
    </div>
  );
}

/** Ordner anlegen oder umbenennen. */
function FolderDialog({
  open,
  onClose,
  campaignId,
  folder,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  folder?: Folder;
  onCreated?: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setName(folder?.name ?? '');
    setError(null);
  }, [open, folder]);
  const save = useMutation({
    mutationFn: async () => {
      if (folder) {
        await patch(`/api/folders/${folder.id}`, { name });
        return folder.id;
      }
      return (await post<{ id: string }>(`/api/campaigns/${campaignId}/folders`, { name })).id;
    },
    onSuccess: async (id) => {
      await queryClient.invalidateQueries({ queryKey: qk('campaign', campaignId) });
      toast(folder ? 'Ordner umbenannt.' : 'Ordner angelegt.');
      onCreated?.(id);
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={folder ? 'Ordner umbenennen' : 'Neuer Ordner'}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn btn-primary"
            disabled={!name.trim() || save.isPending}
            onClick={() => save.mutate()}
          >
            Speichern
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      <Field label="Name" hint="z. B. „HTML5-Banner“, „Videos“, „Social Ads“ oder ein Motiv">
        {(id) => (
          <input
            id={id}
            className="input"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && name.trim() && save.mutate()}
          />
        )}
      </Field>
    </Dialog>
  );
}

function GroupHead({
  group,
  campaign,
  selectedCount,
  onSelectAll,
  onRename,
  onDelete,
  onMove,
}: {
  group: BannerGroup;
  campaign: CampaignDetail;
  selectedCount: number;
  onSelectAll: (select: boolean) => void;
  onRename: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onMove: (folder: Folder, direction: -1 | 1) => void;
}) {
  const folder = group.folder;
  const index = folder ? campaign.folders.findIndex((f) => f.id === folder.id) : -1;
  const allSelected = group.banners.length > 0 && selectedCount === group.banners.length;
  return (
    <div className="section-head">
      <h2 className="section-title">{group.title}</h2>
      {group.subtitle && <span className="small muted">{group.subtitle}</span>}
      <span className="spacer" />
      <span className="small muted">{pluralize(group.banners.length, 'Motiv', 'Motive')}</span>
      {group.banners.length > 0 && (
        <button className="text-link small" onClick={() => onSelectAll(!allSelected)}>
          {allSelected ? 'Auswahl aufheben' : 'Alle auswählen'}
        </button>
      )}
      {folder && (
        <Menu
          trigger={(props) => (
            <button {...props} className="btn btn-ghost btn-sm btn-icon" aria-label={`Ordner ${folder.name}`}>
              <MoreHorizontal size={18} />
            </button>
          )}
        >
          {(close) => (
            <>
              <button className="menu-item" onClick={() => (close(), onRename(folder))}>
                <Pencil size={16} /> Umbenennen
              </button>
              {index > 0 && (
                <button className="menu-item" onClick={() => (close(), onMove(folder, -1))}>
                  <ArrowUp size={16} /> Nach oben
                </button>
              )}
              {index >= 0 && index < campaign.folders.length - 1 && (
                <button className="menu-item" onClick={() => (close(), onMove(folder, 1))}>
                  <ArrowDown size={16} /> Nach unten
                </button>
              )}
              <div className="menu-sep" />
              <button className="menu-item danger" onClick={() => (close(), onDelete(folder))}>
                <Trash2 size={16} /> Ordner löschen
              </button>
            </>
          )}
        </Menu>
      )}
    </div>
  );
}

function ManageCampaign({ campaign }: { campaign: CampaignDetail }) {
  const [tab, setTab] = useState<'banners' | 'comments' | 'activity'>('banners');
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [folderDialog, setFolderDialog] = useState<{ folder?: Folder; moveAfter?: boolean } | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<Folder | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useStoredState<GroupBy>(
    `zeigbanner.groupBy.${campaign.id}`,
    campaign.folders.length ? 'folder' : 'format',
    ['folder', 'format', 'motif'],
  );
  const [hidden, setHidden] = useState<BannerKind[]>([]);
  const [status, setStatus] = useState<StatusFilter>('all');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const refresh = () => queryClient.invalidateQueries({ queryKey: qk('campaign', campaign.id) });

  // Auswahl auf vorhandene Motive beschränken (z. B. nach dem Löschen eines Motivs).
  useEffect(() => {
    setSelected((s) => {
      const ids = new Set(campaign.banners.map((b) => b.id));
      const next = new Set([...s].filter((id) => ids.has(id)));
      return next.size === s.size ? s : next;
    });
  }, [campaign.banners]);

  const archive = useMutation({
    mutationFn: () =>
      patch(`/api/campaigns/${campaign.id}`, {
        status: campaign.status === 'archived' ? 'active' : 'archived',
      }),
    onSuccess: async () => {
      toast(campaign.status === 'archived' ? 'Kampagne wiederhergestellt.' : 'Kampagne archiviert.');
      await refresh();
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const moveSelected = async (folderId: string | null) => {
    try {
      await post(`/api/campaigns/${campaign.id}/move`, { bannerIds: [...selected], folderId });
      const target = campaign.folders.find((f) => f.id === folderId);
      toast(
        `${pluralize(selected.size, 'Motiv', 'Motive')} ${target ? `nach „${target.name}“ verschoben` : 'ohne Ordner abgelegt'}.`,
      );
      setSelected(new Set());
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const moveFolder = async (folder: Folder, direction: -1 | 1) => {
    const ids = campaign.folders.map((f) => f.id);
    const i = ids.indexOf(folder.id);
    [ids[i], ids[i + direction]] = [ids[i + direction], ids[i]];
    try {
      await post(`/api/campaigns/${campaign.id}/folder-order`, { folderIds: ids });
      await refresh();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  const visible = filterBanners(campaign.banners, hidden, status);
  const groups = groupBanners(visible, campaign.folders, groupBy, { keepEmptyFolders: true });
  const toggle = (ids: string[], select: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      for (const id of ids) {
        if (select) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  return (
    <div className="page">
      <Link className="back-link" to="/">
        <ArrowLeft size={16} /> Alle Kampagnen
      </Link>
      <div className="page-head" style={{ marginTop: 14 }}>
        <div className="page-head-text">
          <div className="row-wrap">
            <span className="eyebrow">{campaign.client.name}</span>
            {campaign.visibleToClient ? (
              <span className="chip chip-info">Für Kunde sichtbar</span>
            ) : (
              <span className="chip chip-outline">Intern</span>
            )}
            {campaign.status === 'archived' && <span className="chip chip-dark">Archiviert</span>}
          </div>
          <h1 className="page-title">{campaign.name}</h1>
          {campaign.description && (
            <p className="lead" style={{ whiteSpace: 'pre-wrap' }}>
              {campaign.description}
            </p>
          )}
          {campaign.dueDate && <p className="small muted">Freigabe bis {formatDate(campaign.dueDate)}</p>}
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setSharing(true)}>
            <Share2 size={17} /> Teilen
          </button>
          <Link className="btn btn-outline" to={`/kampagnen/${campaign.id}/vorschau`}>
            <Eye size={17} /> Kundenansicht
          </Link>
          <Menu
            trigger={(props) => (
              <button {...props} className="btn btn-ghost btn-icon" aria-label="Weitere Aktionen">
                <MoreHorizontal size={20} />
              </button>
            )}
          >
            {(close) => (
              <>
                <button
                  className="menu-item"
                  onClick={() => {
                    close();
                    setEditing(true);
                  }}
                >
                  <Pencil size={16} /> Bearbeiten
                </button>
                <a className="menu-item" href={`/api/campaigns/${campaign.id}/download`} onClick={close}>
                  <Download size={16} /> Alle aktuellen Versionen (ZIP)
                </a>
                {campaign.stats.approved > 0 && (
                  <a
                    className="menu-item"
                    href={`/api/campaigns/${campaign.id}/download?only=approved`}
                    onClick={close}
                  >
                    <Download size={16} /> Nur freigegebene (ZIP)
                  </a>
                )}
                <a className="menu-item" href={`/api/campaigns/${campaign.id}/protocol.csv`} onClick={close}>
                  <FileSpreadsheet size={16} /> Freigabeprotokoll (CSV)
                </a>
                <a className="menu-item" href={`/api/campaigns/${campaign.id}/comments.csv`} onClick={close}>
                  <FileSpreadsheet size={16} /> Alle Kommentare (CSV)
                </a>
                <div className="menu-sep" />
                <button
                  className="menu-item"
                  onClick={() => {
                    close();
                    archive.mutate();
                  }}
                >
                  <Archive size={16} /> {campaign.status === 'archived' ? 'Wiederherstellen' : 'Archivieren'}
                </button>
                <button
                  className="menu-item danger"
                  onClick={() => {
                    close();
                    setDeleting(true);
                  }}
                >
                  <Trash2 size={16} /> Löschen
                </button>
              </>
            )}
          </Menu>
        </div>
      </div>

      <Summary campaign={campaign} />

      <div style={{ marginTop: 28 }}>
        <UploadZone
          campaignId={campaign.id}
          folders={campaign.folders}
          big={campaign.banners.length === 0}
          pageDrop
        />
      </div>

      <div style={{ marginTop: 28 }}>
        <Tabs
          value={tab}
          onChange={setTab}
          items={[
            { value: 'banners', label: `Motive (${campaign.banners.length})` },
            {
              value: 'comments',
              label: campaign.stats.openComments
                ? `Kommentare (${campaign.stats.openComments} offen)`
                : 'Kommentare',
            },
            { value: 'activity', label: 'Aktivität' },
          ]}
        />
      </div>

      {tab === 'banners' && (
        <>
          {campaign.banners.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <CollectionBar
                banners={campaign.banners}
                groupBy={groupBy}
                onGroupBy={setGroupBy}
                hidden={hidden}
                onHidden={setHidden}
                status={status}
                onStatus={setStatus}
              >
                <button className="btn btn-outline btn-sm" onClick={() => setFolderDialog({})}>
                  <FolderPlus size={15} /> Neuer Ordner
                </button>
              </CollectionBar>
            </div>
          )}
          {groups.map((group) => {
            const ids = group.banners.map((b) => b.id);
            return (
              <section key={group.key} className="section">
                <GroupHead
                  group={group}
                  campaign={campaign}
                  selectedCount={ids.filter((id) => selected.has(id)).length}
                  onSelectAll={(select) => toggle(ids, select)}
                  onRename={(folder) => setFolderDialog({ folder })}
                  onDelete={setDeletingFolder}
                  onMove={moveFolder}
                />
                {group.banners.length > 0 ? (
                  <div className="banner-grid">
                    {group.banners.map((banner) => (
                      <BannerCard
                        key={banner.id}
                        banner={banner}
                        campaignId={campaign.id}
                        selected={selected.has(banner.id)}
                        onSelect={(select) => toggle([banner.id], select)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="small muted">
                    Noch leer – Motive auswählen und hierher verschieben oder beim Hochladen diesen Ordner
                    wählen.
                  </p>
                )}
              </section>
            );
          })}
          {campaign.banners.length > 0 && visible.length === 0 && (
            <p className="muted" style={{ marginTop: 24 }}>
              Keine Motive für diese Auswahl.
            </p>
          )}
        </>
      )}
      {tab === 'comments' && (
        <div style={{ marginTop: 20 }}>
          <CommentsOverview campaign={campaign} base={`/kampagnen/${campaign.id}`} />
        </div>
      )}
      {tab === 'activity' && (
        <div style={{ marginTop: 18 }}>
          <ActivityList campaignId={campaign.id} />
        </div>
      )}

      {selected.size > 0 && tab === 'banners' && (
        <div className="selection-bar" role="region" aria-label="Auswahl">
          <strong>{pluralize(selected.size, 'Motiv', 'Motive')} ausgewählt</strong>
          <label className="row" style={{ gap: 8 }}>
            <FolderInput size={17} />
            <select
              className="select select-sm"
              value=""
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'new') setFolderDialog({ moveAfter: true });
                else if (value) void moveSelected(value === 'none' ? null : value);
              }}
              aria-label="In Ordner verschieben"
            >
              <option value="">In Ordner verschieben …</option>
              {campaign.folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
              {campaign.folders.length > 0 && <option value="none">Ohne Ordner</option>}
              <option value="new">Neuer Ordner …</option>
            </select>
          </label>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
            <X size={15} /> Auswahl aufheben
          </button>
        </div>
      )}

      <FolderDialog
        open={folderDialog !== null}
        onClose={() => setFolderDialog(null)}
        campaignId={campaign.id}
        folder={folderDialog?.folder}
        onCreated={(id) => {
          if (folderDialog?.moveAfter && selected.size > 0) void moveSelected(id);
        }}
      />
      <ConfirmDialog
        open={deletingFolder !== null}
        onClose={() => setDeletingFolder(null)}
        title="Ordner löschen?"
        message={`„${deletingFolder?.name ?? ''}“ wird gelöscht. Die Motive darin bleiben erhalten und stehen danach ohne Ordner da.`}
        onConfirm={async () => {
          if (!deletingFolder) return;
          await del(`/api/folders/${deletingFolder.id}`);
          await refresh();
          toast('Ordner gelöscht – die Motive sind noch da.');
        }}
      />
      <CampaignDialog open={editing} onClose={() => setEditing(false)} campaign={campaign} />
      <ShareDialog
        open={sharing}
        onClose={() => setSharing(false)}
        campaignId={campaign.id}
        visibleToClient={campaign.visibleToClient}
      />
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        title="Kampagne löschen?"
        message={`„${campaign.name}“ wird mit allen Motiven, Versionen, Kommentaren und Freigaben endgültig gelöscht.`}
        onConfirm={async () => {
          await del(`/api/campaigns/${campaign.id}`);
          await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
          toast('Kampagne gelöscht.');
          navigate('/');
        }}
      />
    </div>
  );
}

export function useCampaign(campaignId: string) {
  return useQuery({
    queryKey: qk('campaign', campaignId),
    queryFn: () => api<CampaignDetail>(`/api/campaigns/${campaignId}`),
  });
}

export function CampaignPage() {
  const { campaignId = '' } = useParams();
  const campaign = useCampaign(campaignId);
  useTitle(campaign.data?.name);
  if (campaign.isLoading) return <Spinner />;
  if (campaign.error || !campaign.data) {
    return (
      <div className="page">
        <Notice kind="error">{errorMessage(campaign.error)}</Notice>
      </div>
    );
  }
  if (campaign.data.permissions.canManage) return <ManageCampaign campaign={campaign.data} />;
  return <PresentationView campaign={campaign.data} base={`/kampagnen/${campaignId}`} />;
}

/** Agentur sieht hier genau das, was Kunden über einen Freigabe-Link zu sehen bekommen. */
export function CampaignPreviewPage() {
  const { campaignId = '' } = useParams();
  const campaign = useCampaign(campaignId);
  useTitle(campaign.data ? `Kundenansicht · ${campaign.data.name}` : undefined);
  if (campaign.isLoading) return <Spinner />;
  if (!campaign.data) return <Notice kind="error">{errorMessage(campaign.error)}</Notice>;
  const asClient: CampaignDetail = {
    ...campaign.data,
    permissions: { ...campaign.data.permissions, canManage: false, canSeeInternal: false },
  };
  return (
    <>
      <div className="notice" style={{ borderRadius: 0, justifyContent: 'center' }}>
        <Eye size={18} />
        <span>
          Kundenansicht – so sieht der Kunde die Kampagne.{' '}
          <Link className="text-link" to={`/kampagnen/${campaignId}`}>
            Zurück zur Bearbeitung
          </Link>
        </span>
      </div>
      <ClientTheme color={campaign.data.client.brandColor} />
      <ClientBrandHeader client={campaign.data.client} campaignName={campaign.data.name} />
      <PresentationView campaign={asClient} base={`/kampagnen/${campaignId}`} />
    </>
  );
}
